// External modules
import axios from 'axios';
import { Formik } from 'formik';
import jsonWebToken from 'jsonwebtoken';
import { isEmpty } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import * as Yup from 'yup';
import universalCookie from 'universal-cookie';

// Internal modules
import ACButton from '../../../atoms/ACButton';
import LoginForm from '../molecules/LoginForm';
import SignUpForm from '../molecules/SignUpForm';

import styles from '../styles/LoginPage.module.scss';

const propTypes = {
    authProperties: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
    errorProperties: PropTypes.object.isRequired,
    Logo: PropTypes.elementType,
    title: PropTypes.string,
    subTitle: PropTypes.string,
};

const defaultProps = {
    Logo: null,
    title: '',
    subTitle: '',
};

const initialLoginValues = {
    password: '',
    username: '',
};

const initialSignupValues = {
    emailAddress: '',
    password: '',
    providerId: '',
    secret: '',
    type: '',
};

const loginValidationSchema = Yup.object({
    password: Yup.string('Enter your password').required("Password can't be empty"),
    username: Yup.string('Enter your username').required('Name is required'),
});

const signUpValidationSchema = Yup.object({
    emailAddress: Yup.string('Enter your email adddress').required('Email address is required'),
    password: Yup.string('Enter your password')
        .required("Password can't be empty")
        .min(8, 'Password needs to have at least 8 characters'),
    secret: Yup.string('Enter the secret').required("Secret can't be empty"),
    type: Yup.string('Enter the type').required("Type can't be empty"),
});

// TODO: Move signup to formik
class LoginPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isSignUpDialogOpen: false,
        };

        this.onChangeSignUpFormOpen = this.onChangeSignUpFormOpen.bind(this);
        this.onChangeUser = this.onChangeUser.bind(this);

        this.onLogin = this.onLogin.bind(this);
        this.onSubmitSignUpFormik = this.onSubmitSignUpFormik.bind(this);
        this.onSubmitLoginFormik = this.onSubmitLoginFormik.bind(this);

        this.renderLoginFormik = this.renderLoginFormik.bind(this);
        this.renderSignUp = this.renderSignUp.bind(this);
        this.renderSignUpFormik = this.renderSignUpFormik.bind(this);
        this.renderSocialAuths = this.renderSocialAuths.bind(this);
    }

    onChangeSignUpFormOpen(isOpen) {
        this.setState({ isSignUpDialogOpen: isOpen });
    }

    onChangeUser(property, value) {
        const { signUpUser } = this.state;
        signUpUser[property] = value;

        this.setState({ signUpUser });
    }

    onLogin(connection, isEmployeeOnly = false) {
        const { authProperties, config, history, errorProperties } = this.props;
        if (!authProperties || !config || !history || !errorProperties) return;

        const { auth, loggedInPath, onChangeLoggedInUserFunc, setUserByEmailAddressFunc } = authProperties;
        const { onChangeErrorMessageFunc } = errorProperties;
        if (!auth || !onChangeLoggedInUserFunc || !onChangeErrorMessageFunc || !setUserByEmailAddressFunc) return;

        const { web: webAuthConfig } = auth.getAuthConfig();
        if (!webAuthConfig || !webAuthConfig.audienceKey) return null;

        const webAuth = auth.getWebAuth(webAuthConfig.audienceKey);

        const authConfig = auth.getAuthConfig();
        if (!authConfig) return null;

        const { client: clientAuthConfig } = authConfig;
        if (!webAuthConfig || !clientAuthConfig) return null;

        const { scopes: keyedScopes } = clientAuthConfig;
        if (!keyedScopes) return null;

        const scopes = keyedScopes[webAuthConfig.audienceKey];
        if (!scopes) return null;

        // https://auth0.com/docs/tokens
        webAuth.popup.authorize({ connection }, (error, authResponse) => {
            if (error) onChangeErrorMessageFunc('error', error);
            console.log('authResponse', authResponse);

            if (error) onChangeErrorMessageFunc(error);
            else {
                if (
                    // TODO: Change this rule accordingly
                    // We are ONLY allowing amazingco email addresses for social login
                    !authResponse.idTokenPayload ||
                    !authResponse.idTokenPayload.email ||
                    (isEmployeeOnly &&
                        authResponse.idTokenPayload.email.indexOf('@amazingco.me') < 0 &&
                        authResponse.idTokenPayload.email.indexOf('@amazingco.com.au') < 0 &&
                        authResponse.idTokenPayload.email.indexOf('@amazingkidsparties.com.au') < 0)
                ) {
                    onChangeErrorMessageFunc('Unauthorised user');
                } else {
                    onChangeErrorMessageFunc(null);

                    const payload = authResponse.idTokenPayload;

                    const loggedInUser = {
                        auth0Id: payload.sub,
                        emailAddress: payload.email,
                        expiryTimestamp: payload.exp,
                        familyName: payload.familyName,
                        givenName: payload.givenName,
                        name: payload.name,
                        picture: payload.picture,
                        types: payload['https://amazingco/types'],
                        username: payload.email,
                    };

                    const oauth2Token = {
                        accessToken: authResponse.accessToken,
                        expiresInSeconds: authResponse.expiresIn,
                        idToken: authResponse.idToken,
                        refreshToken: authResponse.refreshToken,
                        scope: authResponse.scope,
                        state: authResponse.state,
                        tokenType: authResponse.tokenType,
                    };

                    onChangeLoggedInUserFunc({
                        ...loggedInUser,
                        oauth2Token,
                    });

                    // Get AmazingCo user data
                    if (loggedInUser.emailAddress) {
                        setUserByEmailAddressFunc(loggedInUser.emailAddress);
                    }

                    history.push(loggedInPath);
                }
            }
        });
    }

    onSubmitSignUpFormik(values, actions) {
        if (isEmpty(values)) {
            return;
        }

        if (values.secret !== 'amazing123') {
            alert('Invalid secret');
            return;
        }

        const { emailAddress, password, providerId, type } = values;
        if (!emailAddress || !password) return;

        actions.setSubmitting(false);

        const { authProperties, config, history } = this.props;
        if (!authProperties || !config || !history) return;

        const { auth } = authProperties;
        if (!auth) return;

        const { web: webAuthConfig } = auth.getAuthConfig();
        if (!webAuthConfig || !webAuthConfig.audienceKey) return null;

        // TODO: Ask username properly
        const username = `arc-${moment().unix()}`;

        let appMetadata = {};
        switch (type) {
            case 'customer':
                appMetadata = {
                    actions: [],
                    environment: {},
                    objects: [],
                    subject: {
                        types: ['customer'],
                    },
                };
                break;
            case 'concierge':
                appMetadata = {
                    actions: [],
                    environment: {},
                    objects: [],
                    subject: {
                        departments: ['sales-ops'],
                        types: ['employee'],
                        roles: ['local-admin'],
                    },
                };
                break;
            case 'provider':
                appMetadata = {
                    actions: [],
                    environment: {},
                    objects: [],
                    subject: {
                        types: ['provider'],
                    },
                };
                break;
            default:
                break;
        }

        // TODO: In Redux please
        try {
            axios
                .post(webAuthConfig.signUpURL, {
                    email: values.emailAddress,
                    password: values.password,
                    username,
                    connection: webAuthConfig.realm,
                })
                .then((signUpResponse) => {
                    console.log('signUpResponse', signUpResponse);
                    // Update AmazingCo user data
                    const postUserURL = `${webAuthConfig.userAPIURL}/api/v1/users`;
                    const data = {
                        appMetadata,
                        detail: {},
                        emailAddress: values.emailAddress,
                        password: values.password,
                    };
                    if (signUpResponse && signUpResponse.data && signUpResponse.data._id) {
                        data.auth0Id = `auth0|${signUpResponse.data._id}`;
                    }
                    if (providerId) {
                        data.detail.providerId = providerId;
                    }
                    try {
                        axios
                            .post(postUserURL, data)
                            .then((postUserResponse) => {
                                if (postUserResponse && postUserResponse.data && postUserResponse.data.data) {
                                    console.log('postUserResponse.data.data', postUserResponse);
                                    alert('Sign up has been successful!');
                                    this.onChangeSignUpFormOpen(false);
                                } else {
                                    console.log('Failed posting user, Response:', postUserResponse);
                                }
                            })
                            .catch((error) => {
                                alert('Failed posting user, please check your input again!');
                                console.log('Failed posting user, Error:', error);
                            });
                    } catch (error) {
                        alert('Failed posting user, please check your input again!');
                        console.log('Failed posting user, Error:', error);
                    }
                })
                .catch((error) => {
                    alert('Failed signing up, please check your input again!');
                    console.log('Failed posting user, Error:', error);
                });
        } catch (error) {
            alert('Failed signing up, please check your input again!');
            console.log('Failed signing up, Error:', error);
        }
    }

    onSubmitLoginFormik(values, actions) {
        if (isEmpty(values)) {
            return;
        }
        const { username, password } = values;
        if (!username || !password) return;
        actions.setSubmitting(false);
        const { authProperties, config, errorProperties, history } = this.props;
        if (!authProperties || !config || !history || !errorProperties) return;
        const { onChangeErrorMessageFunc } = errorProperties;
        if (!onChangeErrorMessageFunc) return;
        const { auth, loggedInPath, onChangeLoggedInUserFunc, setUserByEmailAddressFunc } = authProperties;
        if ((!auth || !loggedInPath || !onChangeLoggedInUserFunc, !setUserByEmailAddressFunc)) return;
        const { web: webAuthConfig } = auth.getAuthConfig();
        if (!webAuthConfig || !webAuthConfig.audienceKey) return null;
        const webAuth = auth.getWebAuth(webAuthConfig.audienceKey);
        let scope = '';
        const scopes = auth.getClientScopes(webAuthConfig.audienceKey);
        if (scopes && !isEmpty(scopes)) {
            const scopesReducer = (accumulator, currentValue) => {
                return `${accumulator} ${currentValue}`;
            };
            scope = scopes.reduce(scopesReducer);
        }
        // TODO: Clean this up
        // https://auth0.com/docs/tokens
        webAuth.client.login(
            {
                username,
                password,
                realm: webAuthConfig.realm,
                scope,
            },
            function (loginError, loginResponse) {
                let errorMessage = '';
                if (loginResponse) {
                    console.log('loginResponse', loginResponse);
                    const idTokenPayload = jsonWebToken.decode(loginResponse.idToken);

                    webAuth.client.userInfo(loginResponse.accessToken, (userInfoError, userInfoResponse) => {
                        if (userInfoResponse) {
                            console.log('userInfoResponse', userInfoResponse);

                            // const payload = authResponse.idTokenPayload;

                            const loggedInUser = {
                                auth0Id: userInfoResponse.sub,
                                emailAddress: userInfoResponse.email,
                                expiryTimestamp: idTokenPayload.exp,
                                familyName: idTokenPayload.familyName,
                                givenName: idTokenPayload.givenName,
                                name: userInfoResponse.name,
                                picture: userInfoResponse.picture,
                                types: idTokenPayload['https://amazingco/types'],
                                username: userInfoResponse.email,
                            };

                            const oauth2Token = {
                                accessToken: loginResponse.accessToken,
                                expiresInSeconds: loginResponse.expiresIn,
                                idToken: loginResponse.idToken,
                                refreshToken: loginResponse.refreshToken,
                                scope: loginResponse.scope,
                                state: loginResponse.state,
                                tokenType: loginResponse.tokenType,
                            };

                            onChangeLoggedInUserFunc({
                                ...loggedInUser,
                                oauth2Token,
                            });

                            // Get AmazingCo user data
                            if (userInfoResponse.email) {
                                setUserByEmailAddressFunc(userInfoResponse.email);
                            }
                            if(loggedInUser?.types?.includes('employee')) {
                                history.push(loggedInPath);
                            } else if(loggedInUser?.types?.includes('provider')) {
                                alert("Looks like you were trying to access the wrong portal. We've redirected you to the correct Portal. Please try logging in again")
                                const cookies = new universalCookie();
                                const cookiesConfig = config.cookies;
                                if(cookiesConfig.authLoggedInUser) {
                                    cookies.remove(cookiesConfig.authLoggedInUser);
                                }
                                window.location.href = config.providersHQURL;
                            }
                        } else if (userInfoError) {
                            console.error('userInfoError', userInfoError);
                            errorMessage = userInfoError;
                        }
                    });
                } else if (loginError) {
                    console.error('loginError', loginError);
                    if (loginError.description) {
                        errorMessage = loginError.description;
                    } else {
                        errorMessage = loginError;
                    }
                }

                onChangeErrorMessageFunc(errorMessage);
            }
        );
    }

    renderLoginFormik(props) {
        if (!props) return;

        const { authProperties } = this.props;
        if (!authProperties) return;

        const properties = {
            authProperties,
            ...props,
        };
        return <LoginForm props={properties} />;
    }

    // renderAuth0Login() {
    //   return (
    //     <ACButton
    //       key="auth"
    //       onClickFunc={() => this.onLogin()}
    //       style={{
    //         backgroundColor: '#52A8EF',
    //         color: 'white',
    //       }}
    //       text={'Login'}
    //     />
    //   );
    // }

    renderSocialAuths(socialAuths) {
        let socialComponents = [];
        if (!isEmpty(socialAuths)) {
            socialAuths.forEach((socialAuth) => {
                switch (socialAuth.key) {
                    case 'google':
                        const { Icon } = socialAuth;
                        socialComponents.push(
                            <ACButton
                                Icon={Icon}
                                key="google-social-auth"
                                color="#434f69"
                                border="1px solid #434f69"
                                fontFamily="gilroy-regular"
                                fontSize="14px"
                                margin="12px 0 0 0"
                                onClickFunc={() => this.onLogin('google-oauth2', true)}
                                text={`AmazingCo's Login`}
                            />
                        );
                        break;
                    default:
                        break;
                }
            });
        }
        return socialComponents;
    }

    renderSignUp(isAllowed, isOpen) {
        let signUpComponent = null;
        if (isAllowed) {
            signUpComponent = (
                <div
                    style={{
                        margin: '1em',
                        fontFamily: 'gilroy-regular',
                        color: '#44506a',
                    }}
                >
                    <Formik
                        initialValues={initialSignupValues}
                        validationSchema={signUpValidationSchema}
                        onSubmit={this.onSubmitSignUpFormik}
                    >
                        {(props) => this.renderSignUpFormik(props, isOpen)}
                    </Formik>
                    Not a member?
                    <span
                        style={{
                            color: '#52a8ef',
                            cursor: 'pointer',
                            fontFamily: 'gilroy-regular',
                        }}
                        onClick={() => this.onChangeSignUpFormOpen(true)}
                    >
                        &nbsp; Sign Up
                    </span>
                </div>
            );
        }
        return signUpComponent;
    }

    renderSignUpFormik(props, isOpen) {
        if (!props) return;

        const { authProperties } = this.props;
        if (!authProperties) return;

        const properties = {
            authProperties,
            isOpen,
            onChangeSignUpFormOpenFunc: this.onChangeSignUpFormOpen,
            ...props,
        };
        return <SignUpForm props={properties} />;
    }

    render() {
        // const { isSignUpDialogOpen } = this.state;
        const { authProperties, config, errorProperties, Logo, title, subTitle } = this.props;
        if (!authProperties || !config || !errorProperties) return;

        const { auth, loggedInUser, socialAuths, onRedirectToLoggedInPageFunc } = authProperties;
        if (!auth) return;
        if (loggedInUser) onRedirectToLoggedInPageFunc();

        const { errorMessage } = errorProperties;

        const rightStylesCombined = [styles.image, styles.right].join(' ');

        const logoComponent = <Logo className={styles.logo} />;
        return (
            <div className={styles.root}>
                <div className={styles.left}>
                    {logoComponent}
                    <p className={styles.title}>{title}</p>
                    <p className={styles.subTitle}>{subTitle}</p>
                    <p className={styles.error}>{errorMessage}</p>
                    <Formik
                        initialValues={initialLoginValues}
                        validationSchema={loginValidationSchema}
                        onSubmit={this.onSubmitLoginFormik}
                    >
                        {(props) => this.renderLoginFormik(props)}
                    </Formik>
                    {/* {this.renderAuth0Login()} */}
                    {this.renderSocialAuths(socialAuths)}
                    {/* {this.renderSignUp(isSignUpAllowed, isSignUpDialogOpen)} */}
                </div>
                <div className={rightStylesCombined}></div>
            </div>
        );
    }
}

LoginPage.propTypes = propTypes;
LoginPage.defaultProps = defaultProps;

export default LoginPage;
